// Cognira ERP API Service
// Online: calls VGO backend
// Offline: uses IndexedDB cache

import { dbOps, isOnline } from '../db/offline';

const SCHOOL   = process.env.REACT_APP_SCHOOL   || 'http://148.135.136.220:3019';
const HEALTH   = process.env.REACT_APP_HEALTH   || 'http://148.135.136.220:3005';
const FINTECH  = process.env.REACT_APP_FINTECH  || 'http://148.135.136.220:3013';
const BACKEND  = process.env.REACT_APP_BACKEND  || 'https://backend.vgo.global';
const QW       = process.env.REACT_APP_QW       || 'https://qw.vgo.global';

const getToken = () => localStorage.getItem('cognira_token');

const req = async (base, path, method='GET', body=null, auth=true) => {
  const headers = {'Content-Type':'application/json'};
  if (auth && getToken()) headers['Authorization'] = `Bearer ${getToken()}`;
  const res = await fetch(`${base}${path}`, {
    method, headers, body: body ? JSON.stringify(body) : null
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({detail:'Failed'}));
    throw new Error(err.detail || err.error || 'Request failed');
  }
  return res.json();
};

// ── SCHOOL API (online + offline) ────────────────────────────
export const school = {
  dashboard: async () => {
    if (isOnline()) return req(SCHOOL, '/school/dashboard');
    const [students, fees] = await Promise.all([
      dbOps.count('students'),
      dbOps.getAll('fees', 'status', 'pending'),
    ]);
    return {
      total_students: students, pending_fees: fees.length,
      present_today: 0, offline: true
    };
  },

  students: async (className) => {
    if (isOnline()) return req(SCHOOL, `/school/students${className?`?class_name=${className}`:''}`);
    const all = await dbOps.getAll('students');
    const filtered = className ? all.filter(s => s.class_name === className) : all;
    return { students: filtered, total: filtered.length, offline: true };
  },

  addStudent: async (data) => {
    if (isOnline()) return req(SCHOOL, '/school/student', 'POST', data);
    const id = `SCH-${new Date().getFullYear()}-${Math.random().toString(36).substr(2,4).toUpperCase()}`;
    const student = { ...data, student_id: id, status: 'active' };
    await dbOps.add('students', student);
    return { status: 'enrolled_offline', student_id: id, ...student };
  },

  markAttendance: async (data) => {
    if (isOnline()) return req(SCHOOL, '/school/attendance/mark', 'POST', data);
    for (const sid of data.student_ids) {
      await dbOps.add('attendance', {
        student_id: sid, date: data.date, status: data.status
      });
    }
    return { status: 'marked_offline', marked: data.student_ids.length };
  },

  collectFee: async (data) => {
    if (isOnline()) return req(SCHOOL, '/school/fee/collect', 'POST', data);
    const receipt = `RCP-OFFLINE-${Date.now()}`;
    await dbOps.add('fees', { ...data, receipt_no: receipt, status: 'pending_sync' });
    return { status: 'recorded_offline', receipt_no: receipt };
  },

  pendingFees: async () => {
    if (isOnline()) return req(SCHOOL, '/school/fee/pending');
    const fees = await dbOps.getAll('fees', 'status', 'pending');
    return { pending_fees: fees, count: fees.length };
  },

  createExam: async (data) => {
    if (isOnline()) return req(SCHOOL, '/school/exam', 'POST', data);
    await dbOps.add('exams', data);
    return { status: 'created_offline', ...data };
  },

  exams: async () => {
    if (isOnline()) return req(SCHOOL, '/school/exams');
    const exams = await dbOps.getAll('exams');
    return { exams, total: exams.length };
  },

  enterResult: async (data) => {
    if (isOnline()) return req(SCHOOL, '/school/result', 'POST', data);
    await dbOps.add('results', data);
    return { status: 'recorded_offline' };
  },

  reportCard: async (id) => {
    if (isOnline()) return req(SCHOOL, `/school/reportcard/${id}`);
    const students = await dbOps.getAll('students');
    const student = students.find(s => s.student_id === id || s.id === id);
    return { student, results: [], offline: true };
  },

  attReport: async () => {
    if (isOnline()) return req(SCHOOL, '/school/attendance/report');
    const att = await dbOps.getAll('attendance');
    return { report: att, offline: true };
  },

  lowAttendance: async () => {
    if (isOnline()) return req(SCHOOL, '/school/attendance/low');
    return { low_attendance_students: [], offline: true };
  },

  staff: async () => {
    if (isOnline()) return req(SCHOOL, '/school/staff');
    return { staff: [], total: 0 };
  },

  addStaff: async (data) => {
    if (isOnline()) return req(SCHOOL, '/school/staff', 'POST', data);
    await dbOps.add('students', { ...data, type: 'staff' });
    return { status: 'added_offline' };
  },
};

// ── HOSPITAL API ──────────────────────────────────────────────
export const hospital = {
  dashboard: async () => {
    if (isOnline()) return req(HEALTH, '/health/stats');
    const [patients, appts] = await Promise.all([
      dbOps.count('patients'),
      dbOps.count('appointments'),
    ]);
    return { total_patients: patients, total_appointments: appts, offline: true };
  },

  doctors: async () => {
    if (isOnline()) return req(HEALTH, '/health/doctors');
    const doctors = await dbOps.getAll('doctors');
    return { doctors, total: doctors.length };
  },

  addDoctor: async (data) => {
    if (isOnline()) return req(HEALTH, '/health/doctor', 'POST', data);
    await dbOps.add('doctors', data);
    return { status: 'added_offline' };
  },

  patients: async () => {
    const all = await dbOps.getAll('patients');
    return { patients: all, total: all.length };
  },

  addPatient: async (data) => {
    const pid = `PAT-${Date.now().toString().slice(-6)}`;
    const patient = { ...data, patient_id: pid };
    await dbOps.add('patients', patient);
    if (isOnline()) {
      req(HEALTH, '/health/patient', 'POST', patient).catch(() => {});
    }
    return { status: 'registered', patient_id: pid };
  },

  appointments: async () => {
    if (isOnline()) return req(HEALTH, '/health/appointments').catch(async () => {
      const all = await dbOps.getAll('appointments');
      return { appointments: all };
    });
    const all = await dbOps.getAll('appointments');
    return { appointments: all };
  },

  bookAppointment: async (data) => {
    await dbOps.add('appointments', data);
    if (isOnline()) {
      req(HEALTH, '/health/appointment', 'POST', data).catch(() => {});
    }
    return { status: 'booked', offline: !isOnline() };
  },
};

// ── POS API ───────────────────────────────────────────────────
export const pos = {
  products: async () => {
    const all = await dbOps.getAll('products');
    if (all.length === 0 && isOnline()) {
      return req('http://148.135.136.220:3006', '/commerce/products');
    }
    return { products: all, total: all.length };
  },

  addProduct: async (data) => {
    await dbOps.add('products', data);
    return { status: 'added', offline: !isOnline() };
  },

  orders: async () => {
    const all = await dbOps.getAll('orders');
    return { orders: all, total: all.length };
  },

  createOrder: async (items, total, paymentMethod='txc') => {
    const order = {
      items, total_txc: total,
      payment_method: paymentMethod,
      status: isOnline() ? 'completed' : 'pending_sync',
      date: new Date().toISOString()
    };
    const result = await dbOps.add('orders', order);
    return { status: 'created', order_id: result.id, offline: !isOnline() };
  },
};

// ── AUTH ──────────────────────────────────────────────────────
export const auth = {
  login: (data) => req(BACKEND, '/v1/auth/login', 'POST', data, false),
  me:    ()     => req(BACKEND, '/v1/auth/me'),
};

// ── WALLET ────────────────────────────────────────────────────
export const wallet = {
  balance: (uid) => isOnline() ?
    req(BACKEND, `/v1/wallet?user_id=${uid}`) :
    Promise.resolve({ txc_balance: 0, offline: true }),
  pay: (data) => req(BACKEND, '/v1/wallet/send', 'POST', data),
};
